import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Application } from './app/Application.js'
import { suivreLesInteractions } from './shared/api/client.js'
import { estErreurApi } from './shared/api/erreurs.js'
import { suivreLeReseau } from './modules/hors-ligne/etatReseau.js'
import { demarrerSuiviErreurs } from './shared/suivi-erreurs.js'
import './app/tokens.css'

/**
 * Point d'entrée du front AFS.
 *
 * Toute donnée serveur passe par TanStack Query (skill conventions-code).
 * Pas de fetch dans un composant, pas de useEffect de chargement manuel.
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Les données métier (stock, file d'attente, statuts) changent vite.
      // Le temps réel passe par WebSocket ; ce staleTime court est le repli.
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // Hors ligne, une requête ÉCHOUE au lieu d'attendre le réseau en silence :
      // l'écran dit « serveur injoignable » et la coque offre l'accueil hors
      // ligne, plutôt qu'un squelette de chargement sans fin (S74, D1).
      networkMode: 'always',
      // Un refus du serveur — 401, 403, 422 — est une réponse, pas une
      // panne : le rejouer ne changera rien et retarde l'affichage de
      // l'erreur. Seules les pannes réseau méritent une seconde tentative,
      // et elles sont fréquentes sur les sites (CP-12, délestage).
      retry: (nombre, erreur) => {
        if (estErreurApi(erreur) && erreur.code !== 'RESEAU') return false
        // Un délai dépassé a déjà attendu quinze secondes : on le dit tout de suite (S77, C1).
        if (estErreurApi(erreur) && erreur.statut === 408) return false
        return nombre < 1
      },
    },
    mutations: {
      // Une mutation ne se rejoue jamais toute seule : réémettre un
      // encaissement ou une dispensation parce que la réponse s'est perdue
      // est pire que l'échec.
      retry: false,
      // Même raison : un bouton « en cours » qui ne revient jamais n'aide personne.
      networkMode: 'always',
    },
  },
})

// Mode dégradé (CP-12) — le bandeau « hors ligne » de la coque lit cet état, tenu pour tout l'onglet.
suivreLeReseau(queryClient)

// Décision 417 — seuls les gestes de l'agent repoussent la fermeture de session.
suivreLesInteractions(window)

// Mode dégradé (CP-12) — le service worker garde la coquille de l'application, pour qu'un poste
// qui redémarre sans réseau puisse l'ouvrir. En production seulement : en développement, il
// servirait du code périmé.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js')
  })
}

// Suivi des erreurs (ADR 0013) : sans attendre, et sans jamais bloquer le premier rendu.
void demarrerSuiviErreurs()

const racine = document.getElementById('root')
if (!racine) throw new Error('Élément #root introuvable')

createRoot(racine).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Application />
    </QueryClientProvider>
  </StrictMode>,
)
